// This file is auto-generated from Server/Auth/CommunityRolePermissions.cs
// Do not edit manually - changes will be overwritten
import { SparkRoleKey } from "@/models";

/** 
 * Represents a permission that can be granted to community roles.
 */
export interface CommunityPermission {
  /** The community roles that are allowed to perform this action. */
  readonly allowedRoles: readonly SparkRoleKey[];
  /** Whether Global Administrators can perform this action regardless of community role. */
  readonly globalAdminCanDo: boolean;
  /** Human-readable name for display in the permissions list. */
  readonly displayName: string;
}

/** 
 * Centralized permission definitions mirroring Server/Auth/CommunityRolePermissions.cs
 * These define which roles have permission to perform various operations within a community.
 */
export const communityRolePermissions = {
  /** Roles that are allowed to view Prospects in their Community.
      (list and basic details. Recordings, Documents, and Life Story are
      separately permissioned) */
  viewProspects: {
    allowedRoles: [
      SparkRoleKey.CommunityAdmin,
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.RecreationManager,
      SparkRoleKey.SparkCoordinator,
      SparkRoleKey.LifeStoryViewer,
      SparkRoleKey.RecordsViewer
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Prospects: View"
  },

  /** Roles that are allowed to check or uncheck items on the Neighborhood
      Readiness Checklist of a Prospect in their Community. */
  modifyProspectReadinessChecklistItems: {
    allowedRoles: [
      SparkRoleKey.CommunityAdmin,
      SparkRoleKey.CRC,
      SparkRoleKey.DoC
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Prospect: Set Neighborhood Readiness"
  },

  /** Roles that are allowed to view Recordings associated with Prospects
      in their Community (list, details, and audio). */
  viewProspectRecordings: {
    allowedRoles: [
      SparkRoleKey.CommunityAdmin,
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.RecreationManager,
      SparkRoleKey.SparkCoordinator,
      // note no LifeStoryViewer
      SparkRoleKey.RecordsViewer
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Recordings: View Recordings, Transcripts and Summaries"
  },

  /** Roles that are allowed to create or edit Recordings associated with
      Prospects in their Community (record, edit transcript, edit summary,
      edit recording notes, toggle inclusion). TODO: should split out toggle inclusion? */
  addOrEditProspectRecordings: {
    allowedRoles: [
      // note no CommunityAdmin
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.RecreationManager,
      SparkRoleKey.SparkCoordinator
      // note no LifeStoryViewer
      // note no RecordsViewer
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Recordings: Add or Edit Recordings and Summaries"
  },

  /** Roles that are allowed to view Documents associated with Prospects
      in their Community (list, details, and attached file). */
  viewProspectDocuments: {
    allowedRoles: [
      SparkRoleKey.CommunityAdmin,
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.RecreationManager,
      SparkRoleKey.SparkCoordinator
      // note no LifeStoryViewer
      // note no RecordsViewer
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Documents & Notes: View"
  },

  /** Roles that are allowed to create or update Documents associated with
      Prospects in their Community (list and details). */
  addOrEditProspectDocuments: {
    allowedRoles: [
      // note no CommunityAdmin
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.RecreationManager,
      SparkRoleKey.SparkCoordinator
      // note no LifeStoryViewer
      // note no RecordsViewer
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Documents & Notes: Add or Edit"
  },

  /** Roles that are allowed to generate or regenerate the Living Stories
      of Prospects in their Community. */
  generateLivingStory: {
    allowedRoles: [
      // note no Community Admin
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.SparkCoordinator
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Living Story: Generate or Update (AI)"
  },

  /** Roles that are allowed to manually edit the Living Stories of Prospects
      in their Community. */
  editLivingStory: {
    allowedRoles: [
      // note no Community Admin
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.SparkCoordinator
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Living Story: Edit (manual)"
  },

  /** Roles that are allowed to add a Community to or remove a Community
      from the Spark program. */
  setCommunityIsSpark: {
    allowedRoles: [

    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Settings: Add Community to Spark"
  },

  /** Roles that are allowed to view the detailed info on their Community
      including what roles are assigned to what users there. */
  viewCommunityDetails: {
    allowedRoles: [
      SparkRoleKey.CommunityAdmin,
      SparkRoleKey.CRC,
      SparkRoleKey.DoC,
      SparkRoleKey.RecreationManager,
      SparkRoleKey.SparkCoordinator
      // note no LifeStoryViewer
      // note no RecordsViewer
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Settings: View Community Roles"
  },

  /** Roles that are allowed to assign a user to or unassign a user from the
      Community Administrator role in a Community. */
  setCommunityAdmin: {
    allowedRoles: [

    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Settings: Set Community Admins"
  },

  /** Roles that are allowed to assign a user to or unassign a user from any
      roles in a Community other than Community Administrator. */
  setOtherRoleAssignments: {
    allowedRoles: [
      SparkRoleKey.CommunityAdmin
    ] as const satisfies readonly SparkRoleKey[],
    globalAdminCanDo: true,
    displayName: "Settings: Set Other Community Roles"
  }
} as const satisfies Record<string, CommunityPermission>;
